import {
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Wrap,
  WrapItem,
  useDisclosure,
  useMediaQuery,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { About } from "./about";
import { Address } from "./address";
import { BUTTONS_ENG } from "src/common/constants/englishLabels";
import { BUTTONS_HIN } from "src/common/constants/hindiLabels";
import { CommunicationDetails } from "./communicationDetails";
import { EditIcon } from "@chakra-ui/icons";
import { Family } from "./family";
import { GlobalUtils } from "src/utils/globalUtils";
import HeaderComponent from "../register/HeaderComponent";
import { MKS_BACKEND_URL } from "src/utils/env";
import { MarriageRegistration } from "./marriageRegistration";
import { ProfessionalBackground } from "./professionalBackground";
import { ProfileDto } from "src/types/Profile";
import { ProfileService } from "src/rest/profileService";
import { Profile_Tab_English } from "src/common/constants/englishLabels";
import { Profile_Tab_Hin } from "src/common/constants/hindiLabels";
import RightAlignedDrawer from "./rightAlignedModal";
import { RootState } from "src/store/store";
import {UserAvatarIcon} from "../../resources/icons";
import { VerifiedIcon } from "src/resources/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  PROFILE_TABS,
  ProfileTabKey,
  isEditHiddenForTab,
} from "src/pages/profile/profile.constants";

export const Profile = () => {
  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const isHindiLabels = globalInfo.globalLanguage === "HI";
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [userData, setUserData] = useState<ProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState<ProfileTabKey>("ABOUT");

  const [isExtraSmallScreen] = useMediaQuery("(max-width: 630px)");
  const [isSmallScreen] = useMediaQuery(
    "(min-width: 630px) and (max-width: 720px)"
  );
  const [isMediumScreen] = useMediaQuery(
    "(min-width: 720px) and (max-width: 1000px)"
  );

  const navigate = useNavigate();

  // Fetch user profile from backend
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError("");

        const token = GlobalUtils.getToken();
        if (!token) throw new Error("User not authenticated");

        // Consultants: skip the /profile API — use data stored at login
        const role = GlobalUtils.getAppRoleFromToken(token);
        if (role === "CONSULTANT") {
          setLoading(false);
          return;
        }

        // Clear any stale consultant data left over from a previous consultant session
        localStorage.removeItem("consultantInfo");

        const userInfo = GlobalUtils.getUserLoginInfo();
        const emailId = userInfo?.emailId;

        const response = await axios.get(`${MKS_BACKEND_URL}/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: { emailId },
        });

        const data: ProfileDto = response.data as ProfileDto;
        setUserData(data);
      } catch (err: any) {
        setError(err.message || "Failed to fetch user profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSave = async (updatedData: ProfileDto) => {
    try {
      const emailId = GlobalUtils.getUserLoginInfo()?.emailId;
      if (!emailId) throw new Error("User email not found");
      const updatedProfile = await ProfileService.updateProfile(emailId, updatedData);
      setUserData(updatedProfile);
      alert("Profile updated successfully!");
    } catch (error: any) {
      alert("Error updating profile: " + error.message);
    }
  };

  // Prevent back navigation to login
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => navigate("/", { replace: true });
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  

  // Renders correct tab
  const renderTab = (tab: ProfileTabKey) => {
    if (!userData) return null;
    switch (tab) {
      case "ABOUT":
        return <About userData={userData} />;
      case "ADDRESS":
        return <Address userData={userData} />;
      case "COMMUNICATION_DETAILS":
        return <CommunicationDetails userData={userData} />;
      case "PROFESSIONAL_BACKGROUND":
        return <ProfessionalBackground userData={userData} />;
      case "MARRIAGE_REGISTRATION":
        return <MarriageRegistration isHindiLabels={isHindiLabels} />;
      case "FAMILY":
        return <Family isOpenedFromAdmin={false} userData={userData} />;
      default:
        return null;
    }
  };

  // Handle loading/error
  if (loading) return <Spinner size="xl" m="auto" />;
  if (error) return <Heading color="red.500">{error}</Heading>;

  // ── Consultant view ─────────────────────────────────────────────────
  const consultantInfo = GlobalUtils.getConsultantInfo();
  if (consultantInfo) {
    return (
      <Flex direction="column" height="calc(100% - 4rem)" px="2rem">
        <HStack w="100%" mb="2rem">
          <HeaderComponent />
        </HStack>
        <Flex direction="column" alignItems="center">
          <Wrap>
            <WrapItem>
              <UserAvatarIcon width={80} height={80} />
            </WrapItem>
          </Wrap>
          <Flex alignItems="center" gap="1rem">
            <Heading ml="1rem">{consultantInfo.fullName || consultantInfo.email}</Heading>
            <VerifiedIcon />
          </Flex>
          <Divider mt="1rem" width="75%" />
        </Flex>
        <Flex
          direction="column"
          width={isExtraSmallScreen || isSmallScreen ? "100%" : isMediumScreen ? "90%" : "75%"}
          alignSelf="center"
          mt="1rem"
        >
          <Tabs variant="unstyled" width="100%">
            <TabList justifyContent="center" gap={1}>
              <Tab>Marriage Registration</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <MarriageRegistration isHindiLabels={isHindiLabels} isConsultant={true} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Flex>
      </Flex>
    );
  }

  if (!userData) return <Heading>No user data found.</Heading>;

  return (
    <Flex direction="column" height="calc(100% - 4rem)" px="2rem">
      {/* Header */}
      <HStack w="100%" mb="2rem">
        <HeaderComponent />
      </HStack>

      {/* Avatar Section */}
      <Flex direction="column" alignItems="center">
        <Wrap>
          <WrapItem>
            <UserAvatarIcon width={80} height={80} />
          </WrapItem>
        </Wrap>
        <Flex alignItems="center" gap="1rem">
          <Heading ml="1rem">{userData.personalDetails.firstName}</Heading>
          <VerifiedIcon />
        </Flex>
        <Divider mt="1rem" width="75%" />
      </Flex>

      {/* Tabs Section */}
      <Flex
        direction="column"
        width={
          isExtraSmallScreen || isSmallScreen
            ? "100%"
            : isMediumScreen
            ? "90%"
            : "75%"
        }
        alignSelf="center"
        mt="1rem"
      >
        {isExtraSmallScreen ? (
          <>
           <Select
             value={activeTab}
             onChange={(e) =>
             setActiveTab(e.target.value as ProfileTabKey)
           }
           mb="1rem"
          >
         {PROFILE_TABS.map((key) => (
           <option key={key} value={key}>
           {isHindiLabels? Profile_Tab_Hin[key]: Profile_Tab_English[key]}
          </option>
         ))}
          </Select>

                             
            {renderTab(activeTab)}
          </>
        ) : (
          <Tabs variant="unstyled" width="100%">
          <TabList justifyContent="space-between" gap={isSmallScreen ? 2 : 4}>
            {PROFILE_TABS.map((key) => (
            <Tab
            key={key}
            onClick={() => setActiveTab(key)}
            width="20%"
            height={16}
            >
          {isHindiLabels? Profile_Tab_Hin[key]: Profile_Tab_English[key]}
            </Tab>
           ))}
          </TabList>

            <TabPanels>
              <TabPanel>
                <About userData={userData} />
              </TabPanel>
              <TabPanel>
                <Address userData={userData} />
              </TabPanel>
              <TabPanel>
                <CommunicationDetails userData={userData} />
              </TabPanel>
              <TabPanel>
                <ProfessionalBackground userData={userData} />
              </TabPanel>
              <TabPanel>
                <MarriageRegistration />
              </TabPanel>
              <TabPanel>
                <Family isOpenedFromAdmin={false} userData={userData} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}

        {/* Edit Button */}
        <Button
          mb={10}
          hidden={isEditHiddenForTab(activeTab)}
          onClick={onOpen}
          gap="1rem"
          alignSelf="flex-end"
          variant="mks"
        >
          <EditIcon /> {isHindiLabels ? BUTTONS_HIN.EDIT : BUTTONS_ENG.EDIT}
        </Button>
      </Flex>

      {/*Pass userData + onSave to Drawer */}
      <RightAlignedDrawer
        isOpen={isOpen}
        onClose={onClose}
        view={activeTab}
        userData={userData}
        onSave={handleSave}
      />
    </Flex>
  );
};


